/**
 * Quick Add Overlay - Desktop inline add-to-cart
 *
 * Handles direct add-to-cart from product cards without opening modal.
 * Displays overlay on hover with size/color selection.
 */

if (!customElements.get('quick-add-form')) {
  customElements.define('quick-add-form', class QuickAddForm extends HTMLElement {
    constructor() {
      super();

      this.form = this;
      this.submitButton = this.querySelector('.quick-add-overlay__submit');
      this.loadingSpinner = this.querySelector('.quick-add-overlay__loading');
      this.sizeInputs = this.querySelectorAll('input[name^="size-"]');

      this.init();
    }

    init() {
      // Event listener para submit
      if (this.submitButton) {
        this.submitButton.addEventListener('click', this.handleSubmit.bind(this));
      }

      // Event listener para mudança de tamanho
      this.sizeInputs.forEach(input => {
        input.addEventListener('change', this.updateSubmitButton.bind(this));
      });

      // Prevenir propagação de cliques (não abrir página do produto)
      this.addEventListener('click', (e) => {
        e.stopPropagation();
      });

      // Prevenir redirect nos swatches de COR ATUAL
      this.querySelectorAll('.quick-add-overlay__color-swatch.is-selected').forEach(swatch => {
        swatch.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
        });
      });

      // Atualizar estado inicial do botão
      this.updateSubmitButton();
    }

    /**
     * Atualiza estado do botão "Adicionar"
     */
    updateSubmitButton() {
      const selectedSize = this.querySelector('input[name^="size-"]:checked');

      if (this.submitButton) {
        if (selectedSize) {
          this.submitButton.disabled = false;
        } else {
          this.submitButton.disabled = true;
        }
      }
    }

    /**
     * Handle form submit - adiciona ao carrinho
     */
    async handleSubmit(e) {
      e.preventDefault();
      e.stopPropagation();

      const selectedSize = this.querySelector('input[name^="size-"]:checked');
      if (!selectedSize) {
        console.warn('[QuickAddForm] No size selected');
        return;
      }

      const productId = this.dataset.productId;
      const productHandle = this.dataset.productHandle;
      const currentColor = this.dataset.currentColor;
      const selectedSizeValue = selectedSize.value;

      console.log('[QuickAddForm] Adding to cart:', { productHandle, currentColor, size: selectedSizeValue });

      // Encontrar a variante correspondente
      const variantId = await this.findVariantId(productHandle, currentColor, selectedSizeValue);

      if (!variantId) {
        console.error('[QuickAddForm] Variant not found');
        this.showError('Variante não encontrada');
        return;
      }

      // Adicionar ao carrinho
      await this.addToCart(variantId);
    }

    /**
     * Busca o ID da variante baseado em cor + tamanho
     */
    async findVariantId(productHandle, color, size) {
      if (!productHandle) {
        console.error('[QuickAddForm] Missing product handle');
        return null;
      }

      try {
        // Fetch product JSON via ShopifyAPI
        const product = await ShopifyAPI.Product.get(productHandle);

        console.log('[QuickAddForm] Product fetched:', product.title);
        console.log('[QuickAddForm] Looking for:', { color, size });

        // Encontrar variante que combina cor + tamanho
        const variant = product.variants.find(v => {
          // Tentar combinar em ambas as posições (option1/option2)
          const matchesColor = v.option1 === color || v.option2 === color || v.option3 === color;
          const matchesSize = v.option1 === size || v.option2 === size || v.option3 === size;
          const isAvailable = v.available;

          if (matchesColor && matchesSize) {
            console.log('[QuickAddForm] Found variant:', v.title, 'Available:', isAvailable);
          }

          return matchesColor && matchesSize && isAvailable;
        });

        if (variant) {
          console.log('[QuickAddForm] Selected variant ID:', variant.id);
          return variant.id;
        } else {
          console.warn('[QuickAddForm] No matching variant found');
          return null;
        }
      } catch (error) {
        console.error('[QuickAddForm] Error fetching product:', error);
        return null;
      }
    }

    /**
     * Adiciona item ao carrinho via ShopifyAPI
     */
    async addToCart(variantId) {
      // Mostrar loading
      this.setLoading(true);

      try {
        await ShopifyAPI.Cart.add({
          id: variantId,
          quantity: 1
        });

        console.log('[QuickAddForm] Added to cart successfully');

        // Sucesso - mostrar feedback
        this.showSuccess();

        // Buscar cart drawer atualizado e abrir
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          try {
            // Fetch sections atualizadas do Shopify
            const response = await fetch(`${window.routes?.cart_url || '/cart'}?sections=cart-drawer,cart-icon-bubble,cart-icon-bubble-mobile`);
            const sectionsData = await response.json();

            // Shopify retorna sections diretamente, mas renderContents() espera { sections: {...} }
            const data = { sections: sectionsData };

            // Re-renderizar drawer com HTML atualizado (inclui título com count correto)
            if (typeof cartDrawer.renderContents === 'function' && data.sections) {
              cartDrawer.renderContents(data);
            } else {
              // Fallback: apenas abrir drawer
              setTimeout(() => {
                if (typeof cartDrawer.open === 'function') {
                  cartDrawer.open();
                }
              }, 300);
            }
          } catch (error) {
            console.error('[QuickAddForm] Error updating drawer:', error);
            // Fallback: abrir drawer mesmo com erro
            setTimeout(() => {
              if (typeof cartDrawer.open === 'function') {
                cartDrawer.open();
              }
            }, 300);
          }
        }

        // Publicar evento para atualizar outros listeners (CartCounterUpdater)
        if (window.publish && window.PUB_SUB_EVENTS) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-add-overlay' });
        }

      } catch (error) {
        console.error('[QuickAddForm] Error adding to cart:', error);
        this.showError(error.message || 'Erro ao adicionar ao carrinho');
      } finally {
        this.setLoading(false);
      }
    }

    /**
     * Controla estado de loading
     */
    setLoading(isLoading) {
      if (this.submitButton) {
        this.submitButton.disabled = isLoading;
      }

      if (this.loadingSpinner) {
        if (isLoading) {
          this.loadingSpinner.classList.remove('hidden');
        } else {
          this.loadingSpinner.classList.add('hidden');
        }
      }
    }

    /**
     * Mostra feedback de sucesso
     */
    showSuccess() {
      if (this.submitButton) {
        const originalText = this.submitButton.textContent;
        this.submitButton.textContent = 'Adicionado!';
        this.submitButton.classList.add('success');

        setTimeout(() => {
          this.submitButton.textContent = originalText;
          this.submitButton.classList.remove('success');
        }, 2000);
      }
    }

    /**
     * Mostra mensagem de erro
     */
    showError(message) {
      if (this.submitButton) {
        const originalText = this.submitButton.textContent;
        this.submitButton.textContent = 'Erro!';

        setTimeout(() => {
          this.submitButton.textContent = originalText;
        }, 2000);
      }
      console.error('[QuickAddForm] Error:', message);
    }
  });
}
